/**
 * RegisterDevice Component
 *
 * Allows administrators to register new physical device instances based on
 * device enum templates. The registration process includes:
 *
 * 1. Template Selection: Choose from available deviceEnum templates
 * 2. Device Configuration: Enter device-specific information
 * 3. ID Generation: Automatically generates unique device ID
 * 4. Password Creation: Generates secure device password
 *
 * Required Information:
 * - Device template (from deviceEnums)
 * - MAC address (unique network identifier)
 * - Device location/name
 * - Optional: notes, firmware version, installation date
 *
 * The component uses Firestore to:
 * - Load available deviceEnum templates
 * - Register new device instances
 * - Ensure unique device IDs and MAC addresses
 */
import React, { useEffect, useState } from "react";

import { useAuthState } from "react-firebase-hooks/auth";
import { firebaseAuth } from "../../../components/firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  onSnapshot,
  collection,
  query,
  addDoc,
  where,
  getDocs,
} from "firebase/firestore";
import { firebaseStore } from "../../../components/firebase/firestore";
import {
  CircularProgress,
  TextField,
  Button,
  Paper,
  Box,
  Typography,
  Chip,
  Alert,
  Snackbar,
  IconButton,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import moment from "moment";

import { primaryColorTextField } from "../../../components/theme/globalStyles";
import CardDeviceTemplate from "./components/CardDeviceTemplate";

import "./RegisterDevice.css";

function RegisterDevice() {
  const [user, loading] = useAuthState(firebaseAuth);
  const navigate = useNavigate();

  // Loading states
  const [authLoading, setAuthLoading] = useState(true);
  const [deviceEnumsLoading, setDeviceEnumsLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  // Device enum templates
  const [deviceEnums, setDeviceEnums] = useState<any[]>([]);
  const [selectedDeviceEnum, setSelectedDeviceEnum] = useState<any>(null);

  // Registration form fields
  const [deviceName, setDeviceName] = useState<string>("");
  const [macAddress, setMacAddress] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [firmwareVersion, setFirmwareVersion] = useState<string>("");
  const [installationDate, setInstallationDate] = useState<number | null>(
    Date.now(),
  );

  // Generated credentials
  const [deviceId, setDeviceId] = useState<string>("");
  const [devicePassword, setDevicePassword] = useState<string>("");

  // UI feedback
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  /**
   * Authentication check - redirect to login if not authenticated
   */
  useEffect(() => {
    if (user === null && loading === false) {
      navigate("/login");
    } else if (user !== null && loading === false) {
      setAuthLoading(false);
    }
  }, [loading, user, navigate]);

  /**
   * Load available deviceEnum templates from Firestore
   */
  useEffect(() => {
    const q = query(collection(firebaseStore, "deviceEnums"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const tempDeviceEnums: any[] = [];
      querySnapshot.forEach((doc) => {
        tempDeviceEnums.push({ ...doc.data(), id: doc.id });
      });
      setDeviceEnums(tempDeviceEnums);
      setDeviceEnumsLoading(false);
    });

    return unsubscribe;
  }, []);

  /**
   * Generate unique device ID based on device type
   * Format: {deviceType}-{timestamp}-{randomString}
   * Example: esp32-sensor-1708185600-a7b3c9
   */
  const generateDeviceId = async(deviceEnumTemp: any) => {
    if (!deviceEnumTemp) return "";

    const timestamp = Date.now().toString().slice(-10);
    const randomString = Math.random().toString(36).substring(2, 8);
    const deviceType = deviceEnumTemp.type
      .toLowerCase()
      .replace(/\s+/g, "-");

      console.log(`${deviceType}-${timestamp}-${randomString}`);
    return `${deviceType}-${timestamp}-${randomString}`;
  };

  /**
   * Generate secure random password for device
   * Length: 16 characters
   * Includes: uppercase, lowercase, numbers, special characters
   */
  const generatePassword = async() => {
    const length = 16;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";

    // Ensure at least one of each type
    password += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)];
    password += "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)];
    password += "0123456789"[Math.floor(Math.random() * 10)];
    password += "!@#$%^&*"[Math.floor(Math.random() * 8)];

    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }

    // Shuffle the password
    return password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  };

  /**
   * Handle device template selection
   * Automatically generates new credentials when template changes
   */
  const handleTemplateSelect = async (deviceEnum: any) => {
    setSelectedDeviceEnum(deviceEnum);
    setDeviceName(deviceEnum.name);
    setErrorMessage("");

    // Generate new credentials
    const newId = await generateDeviceId(deviceEnum);
      const newPassword = await generatePassword();
      console.log("Generated Device ID:", newId);
      console.log("Generated Password:", newPassword);
      setDeviceId(newId);
      setDevicePassword(newPassword);
  };

  /**
   * Regenerate device ID
   */
  const handleRegenerateId = async () => {
    const newId = await generateDeviceId(selectedDeviceEnum);
    setDeviceId(newId);
  };

  /**
   * Regenerate device password
   */
  const handleRegeneratePassword = async () => {
    const newPassword = await generatePassword();
    setDevicePassword(newPassword);
  };

  /**
   * Copy text to clipboard with user feedback
   */
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setSnackbarMessage(`${label} copied to clipboard`);
    setSnackbarOpen(true);
  };

  /**
   * Validate MAC address format
   * Accepts formats: XX:XX:XX:XX:XX:XX or XX-XX-XX-XX-XX-XX
   */
  const isValidMacAddress = (mac: string): boolean => {
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    return macRegex.test(mac);
  };

  /**
   * Check if MAC address already exists in database
   */
  const checkMacAddressExists = async (mac: string): Promise<boolean> => {
    const q = query(
      collection(firebaseStore, "devices"),
      where("macAddress", "==", mac),
    );
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  /**
   * Validate form before submission
   */
  const validateForm = (): boolean => {
    if (!selectedDeviceEnum) {
      setErrorMessage("Please select a device template");
      return false;
    }

    if (!macAddress.trim()) {
      setErrorMessage("MAC address is required");
      return false;
    }

    if (!isValidMacAddress(macAddress)) {
      setErrorMessage(
        "Invalid MAC address format. Use XX:XX:XX:XX:XX:XX or XX-XX-XX-XX-XX-XX",
      );
      return false;
    }

    if (!deviceName.trim()) {
      setErrorMessage("Device name is required");
      return false;
    }

    if (!deviceId) {
      setErrorMessage("Device ID generation failed");
      return false;
    }

    if (!devicePassword) {
      setErrorMessage("Password generation failed");
      return false;
    }

    return true;
  };

  /**
   * Register new device to Firestore
   */
  const handleRegisterDevice = async () => {
    setErrorMessage("");

    if (!validateForm()) return;

    // Check if MAC address already exists
    setRegistering(true);
    const macExists = await checkMacAddressExists(macAddress);

    if (macExists) {
      setErrorMessage("A device with this MAC address is already registered");
      setRegistering(false);
      return;
    }

    try {
      const deviceData = {
        // Template reference
        deviceEnumId: selectedDeviceEnum.id,
        deviceEnumType: selectedDeviceEnum.type,

        // Device identifiers
        deviceId: deviceId,
        macAddress: macAddress,
        password: devicePassword, // In production, this should be hashed

        // Device information
        name: deviceName,
        os: selectedDeviceEnum.os,

        // Optional fields
        notes: notes || "",
        firmwareVersion: firmwareVersion || "",
        installationDate: installationDate || Date.now(),

        // Metadata
        registeredAt: Date.now(),
        registeredBy: user?.uid || "",
        status: "active",
        lastSeen: null,

        // Copy template configuration
        sensors: selectedDeviceEnum.sensors || [],
        io: selectedDeviceEnum.io || [],
        hardware: selectedDeviceEnum.hardware || [],
      };

      await addDoc(collection(firebaseStore, "devices"), deviceData);

      setSnackbarMessage(
        `Device "${deviceName}" registered successfully! Device ID: ${deviceId}`,
      );
      setSnackbarOpen(true);

      // Reset form
      resetForm();
    } catch (error) {
      console.error("Error registering device:", error);
      setErrorMessage("Failed to register device. Please try again.");
    } finally {
      setRegistering(false);
    }
  };

  /**
   * Reset form to initial state
   */
  const resetForm = () => {
    setSelectedDeviceEnum(null);
    setDeviceName("");
    setMacAddress("");
    setNotes("");
    setFirmwareVersion("");
    setInstallationDate(Date.now());
    setDeviceId("");
    setDevicePassword("");
    setErrorMessage("");
  };

  // Show loading spinner while checking authentication or loading data
  if (authLoading || deviceEnumsLoading) {
    return (
      <div className="register-device-container">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress sx={{ color: "#44a99a" }} />
        </Box>
      </div>
    );
  }

  return (
    <div className="register-device-container">
      <Box
        sx={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: 0,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          flexDirection: "column",
          marginBottom: '60px',
        }}
      >
        {/* Header */}
        <Box sx={{ marginBottom: 4, width: "96%" }}>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Register New Device
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select a device template and configure the device details to
            register a new device instance.
          </Typography>
        </Box>

        {/* Step 1: Device Template Selection */}
        {!selectedDeviceEnum && (
          <Box
            sx={{
              width: "96%",
              margin: 0,
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight={500}>
              Step 1: Select Device Template
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ marginBottom: 3 }}
            >
              Choose the type of device you want to register
            </Typography>

            {deviceEnums.length === 0 ? (
              <Alert severity="info">
                No device templates available. Please create device enums first.
              </Alert>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 0,
                  width: "100%",
                  margin: 0,
                }}
              >
                {deviceEnums.map((deviceEnum) => (
                  <CardDeviceTemplate
                    key={deviceEnum.id}
                    deviceEnum={deviceEnum}
                    onSelect={handleTemplateSelect}
                    isSelected={selectedDeviceEnum?.id === deviceEnum.id}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Step 2: Device Registration Form */}
        {selectedDeviceEnum && (
          <Box>
            {/* Selected Template Display */}
            <Paper
              elevation={0}
              sx={{
                padding: 2,
                marginBottom: 3,
                backgroundColor: "#f5f5f5",
                border: "1px solid #e0e0e0",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Selected Template
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {selectedDeviceEnum.name}
                  </Typography>
                  <Box sx={{ marginTop: 1, display: "flex", gap: 1 }}>
                    <Chip label={selectedDeviceEnum.type} size="small" />
                    <Chip
                      label={selectedDeviceEnum.os}
                      size="small"
                      color="primary"
                    />
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedDeviceEnum(null)}
                >
                  Change Template
                </Button>
              </Box>
            </Paper>

            <Typography variant="h6" gutterBottom fontWeight={500}>
              Step 2: Configure Device
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Generated Credentials Section */}
              <Paper elevation={2} sx={{ padding: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Generated Credentials
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ marginBottom: 2 }}
                >
                  These credentials are automatically generated and unique to
                  this device
                </Typography>

                {/* Device ID */}
                <Box sx={{ marginBottom: 2 }}>
                  <Typography variant="body2" fontWeight={500} gutterBottom>
                    Device ID
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      fullWidth
                      value={deviceId}
                      InputProps={{
                        readOnly: true,
                        sx: { fontFamily: "monospace", fontSize: "0.9rem" },
                      }}
                      size="small"
                      sx={primaryColorTextField}
                    />
                    <IconButton
                      onClick={handleRegenerateId}
                      color="primary"
                      title="Regenerate ID"
                    >
                      <RefreshIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => copyToClipboard(deviceId, "Device ID")}
                      color="primary"
                      title="Copy to clipboard"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Device Password */}
                <Box>
                  <Typography variant="body2" fontWeight={500} gutterBottom>
                    Device Password
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                      fullWidth
                      value={devicePassword}
                      InputProps={{
                        readOnly: true,
                        sx: { fontFamily: "monospace", fontSize: "0.9rem" },
                      }}
                      size="small"
                      sx={primaryColorTextField}
                    />
                    <IconButton
                      onClick={handleRegeneratePassword}
                      color="primary"
                      title="Regenerate password"
                    >
                      <RefreshIcon />
                    </IconButton>
                    <IconButton
                      onClick={() =>
                        copyToClipboard(devicePassword, "Password")
                      }
                      color="primary"
                      title="Copy to clipboard"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Alert severity="warning" sx={{ marginTop: 2 }}>
                  Make sure to save these credentials! You'll need them to
                  configure the physical device.
                </Alert>
              </Paper>

              {/* Device Information Section */}
              <Paper elevation={2} sx={{ padding: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Device Information
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Device Name */}
                  <TextField
                    label="Device Name *"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    fullWidth
                    sx={primaryColorTextField}
                    helperText="A friendly name for this device"
                  />

                  {/* MAC Address */}
                  <TextField
                    label="MAC Address *"
                    value={macAddress}
                    onChange={(e) =>
                      setMacAddress(e.target.value.toUpperCase())
                    }
                    fullWidth
                    sx={primaryColorTextField}
                    placeholder="XX:XX:XX:XX:XX:XX"
                    helperText="The device's unique hardware address"
                    error={
                      macAddress.length > 0 && !isValidMacAddress(macAddress)
                    }
                  />

                  {/* Firmware Version */}
                  <TextField
                    label="Firmware Version"
                    value={firmwareVersion}
                    onChange={(e) => setFirmwareVersion(e.target.value)}
                    fullWidth
                    sx={primaryColorTextField}
                    placeholder="e.g., v1.2.3"
                  />

                  {/* Installation Date */}
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label="Installation Date"
                      value={moment(installationDate)}
                      onChange={(newValue) => {
                        if (newValue) {
                          //@ts-ignore
                          setInstallationDate(newValue.valueOf());
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          sx={primaryColorTextField}
                        />
                      )}
                    />
                  </LocalizationProvider>

                  {/* Notes */}
                  <TextField
                    label="Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    sx={primaryColorTextField}
                    helperText="Any additional information about this device"
                  />
                </Box>
              </Paper>

              {/* Error Message */}
              {errorMessage && (
                <Alert severity="error" onClose={() => setErrorMessage("")}>
                  {errorMessage}
                </Alert>
              )}

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button variant="outlined" onClick={resetForm} size="large">
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRegisterDevice}
                  disabled={registering}
                  size="large"
                >
                  {registering ? "Registering..." : "Register Device"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </div>
  );
}

export default RegisterDevice;
